import { useSyncExternalStore } from "react";
import type { CmmdRuntimeThreadReplica } from "@cmmd-forge/contracts";

import { cmmdApiClient } from "../environments/cmmdApi";

type Listener = () => void;

export type CmmdRuntimeThreadReplicaSnapshot =
  | { readonly kind: "idle" }
  | { readonly kind: "loading"; readonly replica: CmmdRuntimeThreadReplica | null }
  | { readonly kind: "ready"; readonly replica: CmmdRuntimeThreadReplica }
  | {
      readonly kind: "error";
      readonly replica: CmmdRuntimeThreadReplica | null;
      readonly message: string;
    };

const IDLE_SNAPSHOT: CmmdRuntimeThreadReplicaSnapshot = { kind: "idle" };
const DEFAULT_REPLICA_LOAD_TIMEOUT_MS = 15_000;

function replicaKey(runtimeId: string, threadId: string): string {
  return `${runtimeId}\0${threadId}`;
}

/**
 * Only an explicit authorization failure may destroy readable history.
 *
 * 401/403 are the control plane's revocation signals — its access service
 * throws and the error handler maps that to 403 — so dropping the cached
 * transcript is the point.
 *
 * 404 deliberately is NOT in this set. It means "not found in the scope this
 * request resolved to", and that scope was routinely wrong: the transcript
 * route scoped reads by the caller's ACTIVE workspace while the runtime
 * belonged to another one, so opening a cross-workspace thread 404'd on
 * history that was present and intact (CMMD FM-120). Treating that as
 * revocation wiped the transcript the user was already reading, and because
 * Retry re-issued the same mis-scoped request, the session could not recover.
 * Replication lag and a runtime that has not resolved yet produce the same
 * transient 404.
 *
 * Retaining is not the same as inventing: with nothing cached the snapshot
 * still reports an empty transcript, and a later 401/403 clears whatever a 404
 * kept.
 */
function shouldDiscardCachedReplica(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const status = (error as { readonly status?: unknown }).status;
  return status === 401 || status === 403;
}

function replicaFromSnapshot(
  snapshot: CmmdRuntimeThreadReplicaSnapshot | undefined,
): CmmdRuntimeThreadReplica | null {
  if (!snapshot || snapshot.kind === "idle") return null;
  return snapshot.replica;
}

function transcriptErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Could not load the synced thread transcript.";
}

async function withDeadline<T>(request: Promise<T>, timeoutMs: number): Promise<T> {
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(
      () => reject(new Error("Saved history did not respond. Retry the request.")),
      timeoutMs,
    );
  });
  try {
    return await Promise.race([request, deadline]);
  } finally {
    if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
  }
}

export function createCmmdRuntimeThreadReplicaResource(
  loadReplica: (runtimeId: string, threadId: string) => Promise<CmmdRuntimeThreadReplica> = (
    runtimeId,
    threadId,
  ) => cmmdApiClient.getRuntimeThreadReplica(runtimeId, threadId),
  loadTimeoutMs = DEFAULT_REPLICA_LOAD_TIMEOUT_MS,
) {
  const snapshots = new Map<string, CmmdRuntimeThreadReplicaSnapshot>();
  const loadGenerations = new Map<string, number>();
  const listeners = new Set<Listener>();

  function emit(key: string, snapshot: CmmdRuntimeThreadReplicaSnapshot): void {
    snapshots.set(key, snapshot);
    for (const listener of listeners) listener();
  }

  return {
    subscribe(listener: Listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    read(runtimeId: string, threadId: string): CmmdRuntimeThreadReplicaSnapshot {
      return snapshots.get(replicaKey(runtimeId, threadId)) ?? IDLE_SNAPSHOT;
    },
    async load(runtimeId: string, threadId: string): Promise<CmmdRuntimeThreadReplica> {
      const key = replicaKey(runtimeId, threadId);
      const generation = (loadGenerations.get(key) ?? 0) + 1;
      loadGenerations.set(key, generation);
      const previousReplica = replicaFromSnapshot(snapshots.get(key));
      emit(key, { kind: "loading", replica: previousReplica });
      try {
        const replica = await withDeadline(loadReplica(runtimeId, threadId), loadTimeoutMs);
        if (loadGenerations.get(key) !== generation) {
          throw new Error("Transcript load was superseded by a newer authorization check.");
        }
        emit(key, { kind: "ready", replica });
        return replica;
      } catch (error) {
        if (loadGenerations.get(key) !== generation) throw error;
        emit(key, {
          kind: "error",
          replica: shouldDiscardCachedReplica(error) ? null : previousReplica,
          message: transcriptErrorMessage(error),
        });
        throw error;
      }
    },
  };
}

export const cmmdRuntimeThreadReplicaResource = createCmmdRuntimeThreadReplicaResource();

export function useCmmdRuntimeThreadReplicaSnapshot(
  runtimeId: string,
  threadId: string,
): CmmdRuntimeThreadReplicaSnapshot {
  return useSyncExternalStore(
    cmmdRuntimeThreadReplicaResource.subscribe,
    () => cmmdRuntimeThreadReplicaResource.read(runtimeId, threadId),
    () => cmmdRuntimeThreadReplicaResource.read(runtimeId, threadId),
  );
}
